// src/components/PseudoDicomAnonymizerPage/shared/DicomTagSelector.jsx
import React, { useState } from 'react';
import {
  FormGroup, FormControlLabel, Checkbox, Typography, Box, Stack, Divider, Paper, Chip, TextField, Button
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from 'react-i18next';

// Centralized list of tags! Update here, and it updates everywhere.
export const COMMON_DICOM_TAGS = [
  { key: 'PatientName', label: 'Meno pacienta (Patient Name)' },
  { key: 'PatientID', label: 'ID pacienta (Patient ID)' },
  { key: 'PatientBirthDate', label: 'Dátum narodenia (Patient Birth Date)' },
  { key: 'PatientSex', label: 'Pohlavie (Patient Sex)' },
  { key: 'StudyDescription', label: 'Popis štúdie (Study Description)' },
  { key: 'SeriesDescription', label: 'Popis série (Series Description)' },
  { key: 'InstitutionName', label: 'Názov inštitúcie (Institution Name)' },
  { key: 'ReferringPhysicianName', label: 'Odosielajúci lekár (Referring Physician)' },
  { key: 'SOPInstanceUID', label: 'Unikátne ID (SOP / Study / Series UIDs)' },
];

const DicomTagSelector = ({ options, onChangeOptions }) => {
  const { t } = useTranslation();
  const [customTagInput, setCustomTagInput] = useState('');

  const currentTags = options.tags_to_pseudonymize || [];

  // --- Handlers ---
  const handleToggleTag = (tagKey) => (event) => {
    const checked = event.target.checked;
    let newTags = [...currentTags];
    
    const keysToToggle = tagKey === 'SOPInstanceUID' 
      ? ['SOPInstanceUID', 'StudyInstanceUID', 'SeriesInstanceUID'] 
      : [tagKey];

    if (checked) {
      newTags = [...new Set([...newTags, ...keysToToggle])];
    } else {
      newTags = newTags.filter(t => !keysToToggle.includes(t));
    }
    onChangeOptions({ ...options, tags_to_pseudonymize: newTags });
  };

  const handleToggleAllTags = () => {
    if (currentTags.length >= COMMON_DICOM_TAGS.length) {
      const customOnly = currentTags.filter(tag => !COMMON_DICOM_TAGS.some(c => c.key === tag || tag.includes('UID')));
      onChangeOptions({ ...options, tags_to_pseudonymize: customOnly });
    } else {
      const allCommonKeys = COMMON_DICOM_TAGS.flatMap(t => 
        t.key === 'SOPInstanceUID' ? ['SOPInstanceUID', 'StudyInstanceUID', 'SeriesInstanceUID'] : [t.key]
      );
      onChangeOptions({ ...options, tags_to_pseudonymize: [...new Set([...currentTags, ...allCommonKeys])] });
    }
  };

  const handleAddCustomTag = () => {
    const trimmed = customTagInput.trim();
    if (!trimmed) return;
    if (!currentTags.includes(trimmed)) {
      onChangeOptions({ ...options, tags_to_pseudonymize: [...currentTags, trimmed] });
    }
    setCustomTagInput('');
  };

  const handleRemoveCustomTag = (tagToRemove) => {
    onChangeOptions({
      ...options,
      tags_to_pseudonymize: currentTags.filter(t => t !== tagToRemove)
    });
  };

  const handleToggleDb = (e) => {
    onChangeOptions({ ...options, save_to_db: e.target.checked });
  };

  // Derived state
  const customTags = currentTags.filter(tag => 
    !COMMON_DICOM_TAGS.some(c => c.key === tag) && 
    tag !== 'StudyInstanceUID' && 
    tag !== 'SeriesInstanceUID'
  );
  const isAllCommonSelected = COMMON_DICOM_TAGS.every(t => currentTags.includes(t.key));

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="subtitle2">
          {t('DicomOptionsDialog.selectTags', 'Základné tagy:')}
        </Typography>
        <Button size="small" onClick={handleToggleAllTags}>
          {isAllCommonSelected ? t('common.deselectAll', 'Zrušiť výber') : t('common.selectAll', 'Vybrať všetko')}
        </Button>
      </Stack>
      
      <Paper variant="outlined" sx={{ p: 2, maxHeight: 250, overflow: 'auto', mb: 3 }}>
        <FormGroup>
          {COMMON_DICOM_TAGS.map((tag) => {
            const isChecked = currentTags.includes(tag.key);
            return (
              <FormControlLabel
                key={tag.key}
                control={<Checkbox checked={isChecked} onChange={handleToggleTag(tag.key)} size="small" />}
                label={<Typography variant="body2">{tag.label}</Typography>}
              />
            );
          })}
        </FormGroup>
      </Paper>

      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        {t('DicomOptionsDialog.customTagsTitle', 'Vlastné tagy (DICOM Keywords):')}
      </Typography>
      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        <TextField
          size="small"
          fullWidth
          placeholder={t('DicomOptionsDialog.customTagsPlaceholder', 'Napr. PatientWeight, InstitutionAddress...')}
          value={customTagInput}
          onChange={(e) => setCustomTagInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleAddCustomTag();
            }
          }}
        />
        <Button 
          variant="outlined" 
          onClick={handleAddCustomTag}
          disabled={!customTagInput.trim()}
          sx={{ minWidth: 'auto', px: 2 }}
        >
          <AddIcon fontSize="small" />
        </Button>
      </Stack>

      {customTags.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
          {customTags.map((tag) => (
            <Chip
              key={tag}
              label={tag}
              onDelete={() => handleRemoveCustomTag(tag)}
              size="small"
              color="primary"
              variant="outlined"
            />
          ))}
        </Box>
      )}

      <Divider sx={{ my: 2 }} />
      
      <FormGroup>
        <FormControlLabel
          control={<Checkbox checked={!!options.save_to_db} onChange={handleToggleDb} />}
          label={t('admin.pii.options.saveToDb', 'Uložiť súbory do databázy')}
        />
      </FormGroup>
    </Box>
  );
};

export default DicomTagSelector;